import React from 'react';
import { motion } from 'framer-motion';
import { fadeInUp, staggerContainer } from '../hooks/useScrollAnimation';
import { MapPin, GraduationCap, Briefcase, Award } from 'lucide-react';
export function AboutSection() {
  const highlights = [{
    icon: <Briefcase className="w-4 h-4" data-id="element-14" />,
    label: '30+ Years',
    sub: 'Enterprise Technology'
  }, {
    icon: <Award className="w-4 h-4" data-id="element-15" />,
    label: 'Fortune 100',
    sub: 'CTO Experience'
  }, {
    icon: <GraduationCap className="w-4 h-4" data-id="element-16" />,
    label: 'Wharton',
    sub: 'Executive CTO Program'
  }, {
    icon: <MapPin className="w-4 h-4" data-id="element-17" />,
    label: 'Plano, TX',
    sub: 'Open to Relocation'
  }];
  return <section id="about" className="py-20 md:py-32 bg-dark-bg border-t border-dark-border/30" data-id="element-18">
      <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-19">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={staggerContainer} data-id="element-20">
          <motion.div variants={fadeInUp} className="mb-16" data-id="element-21">
            <span className="font-sans text-gold/80 tracking-widest uppercase text-xs font-semibold mb-3 block" data-id="element-22">
              About
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-white" data-id="element-23">
              The Executive Behind the Numbers
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16 items-start" data-id="element-24">
            {/* Left Column */}
            <motion.div variants={fadeInUp} className="lg:col-span-2 flex flex-col gap-8" data-id="element-25">
              {/* Headshot */}
              <div className="flex items-center gap-5" data-id="element-26">
                <div className="w-24 h-24 rounded-xl border-2 border-gold/40 overflow-hidden flex-shrink-0" data-id="element-27">
                  <img src="https://cdn.magicpatterns.com/uploads/dmJTA2YsaWCaEwYeBYJdDH/Michael_Parks_284x210_1.jpg" alt="Michael Parks" className="w-full h-full object-cover object-center scale-110" data-id="element-28" />
                </div>
                <div data-id="element-29">
                  <div className="text-white font-bold text-xl" data-id="element-30">
                    Michael Parks
                  </div>
                  <div className="text-gold/70 text-sm mt-1" data-id="element-31">
                    Chief Technology Officer
                  </div>
                  <div className="flex items-center gap-2 mt-2" data-id="element-32">
                    <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" data-id="element-33"></span>
                    <span className="text-green-400 text-xs font-medium" data-id="element-34">
                      Open to Opportunities
                    </span>
                  </div>
                </div>
              </div>

              {/* Highlight Stats */}
              <div className="grid grid-cols-2 gap-3" data-id="element-35">
                {highlights.map((h, i) => <div key={i} className="bg-dark-card border border-dark-border rounded-lg p-4 flex flex-col gap-2" data-id="element-36">
                    <div className="text-gold/70" data-id="element-37">{h.icon}</div>
                    <div className="text-white font-bold text-sm" data-id="element-38">
                      {h.label}
                    </div>
                    <div className="text-slate-500 text-xs" data-id="element-39">{h.sub}</div>
                  </div>)}
              </div>

              {/* Education */}
              <div className="bg-dark-card border border-gold/20 rounded-xl p-5" data-id="element-40">
                <div className="text-gold/80 text-xs uppercase tracking-wider font-semibold mb-3" data-id="element-41">
                  Education
                </div>
                <div className="space-y-3" data-id="element-42">
                  <div data-id="element-43">
                    <div className="text-white font-semibold text-sm" data-id="element-44">
                      The Wharton School, University of Pennsylvania
                    </div>
                    <div className="text-slate-400 text-xs mt-0.5" data-id="element-45">
                      Executive Chief Technology Officer Program · 2023
                    </div>
                  </div>
                  <div className="border-t border-dark-border pt-3" data-id="element-46">
                    <div className="text-white font-semibold text-sm" data-id="element-47">
                      Arizona State University
                    </div>
                    <div className="text-slate-400 text-xs mt-0.5" data-id="element-48">
                      Information Technology Studies · 2013–2015
                    </div>
                  </div>
                  <div className="border-t border-dark-border pt-3" data-id="element-49">
                    <div className="text-white font-semibold text-sm" data-id="element-50">
                      St Edwards University
                    </div>
                    <div className="text-slate-400 text-xs mt-0.5" data-id="element-51">
                      Engineering Degree · 2010–2013
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right Column — Bio */}
            <motion.div variants={fadeInUp} className="lg:col-span-3 space-y-6" data-id="element-52">
              <p className="text-slate-200 text-lg leading-relaxed" data-id="element-53">
                I am an Enterprise Technology Executive with more than 30 years
                of experience leading large-scale infrastructure, cloud, and
                platform engineering organizations within highly regulated
                environments. My career has been defined by a proven record of
                driving enterprise modernization, strengthening resiliency, and
                aligning technology strategy with business and regulatory
                mandates.
              </p>

              <p className="text-slate-300 leading-relaxed" data-id="element-54">
                I lead global organizations of up to 1,700 professionals with
                direct accountability for budgets exceeding $1.2B. I am
                recognized for executing complex transformation initiatives
                including hybrid cloud adoption, data center modernization,
                cyber resiliency architecture, and large-scale automation
                programs that enhance operational stability and regulatory
                posture.
              </p>

              <p className="text-slate-300 leading-relaxed" data-id="element-55">
                Most recently serving as EVP & CTO at Truist and previously at
                Wells Fargo, I have delivered more than $140M in cumulative
                efficiency gains, built four new enterprise data centers while
                exiting eight legacy facilities, and achieved eight effective
                regulatory audit ratings with no adverse findings across OCC,
                FFIEC, and Federal Reserve oversight.
              </p>

              <p className="text-slate-300 leading-relaxed" data-id="element-56">
                I operate with disciplined executive oversight, balancing
                innovation and risk management while delivering measurable
                efficiency gains and sustained enterprise performance. I am
                trusted by senior leadership to lead mission-critical platforms,
                navigate regulatory scrutiny, and position technology
                infrastructure as a strategic enabler of growth and long-term
                resilience.
              </p>

              <div className="pt-4 border-t border-dark-border" data-id="element-57">
                <div className="text-gold/80 text-xs uppercase tracking-wider font-semibold mb-4" data-id="element-58">
                  Core Strengths
                </div>
                <div className="flex flex-wrap gap-2" data-id="element-59">
                  {['Hybrid Cloud Strategy', 'Cyber Resiliency', 'Regulatory Compliance (OCC/FFIEC)', 'Infrastructure Modernization', 'Data Center Strategy', 'DevOps Transformation', 'Budget Oversight', 'Organizational Leadership'].map(tag => <span key={tag} className="px-3 py-1.5 bg-dark-card border border-dark-border rounded-full text-slate-300 text-xs font-medium" data-id="element-60">
                      {tag}
                    </span>)}
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>;
}